import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, StorefrontIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  NeighbourhoodPerk,
  useCreateNeighbourhoodPerk,
  useUpdateNeighbourhoodPerk,
} from '@/api/neighbourhood-perks'
import { AvatarFormField, SelectFormField } from '@/components/form'
import { NumericInputFormField } from '@/components/form/numeric-input-form-field'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { SelectOption } from '@/components/ui/select'
import { toastFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { useDirtyFormGuard } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'
import { normalizeLink } from '@/utils/link'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { PersonFormLayout } from '../../../../shared/person-form-layout'
import {
  AMOUNT_DISCOUNT_MAX,
  buildDiscount,
  DISCOUNT_SYMBOLS,
  DiscountType,
  getPerkFormDefaults,
  makePerkFormSchema,
  PERCENT_DISCOUNT_MAX,
  PERK_DESCRIPTION_MAX_LENGTH,
  PERK_DISCOUNT_LENGTH,
  PERK_TEXT_MAX_LENGTH,
  PerkFormData,
  PerkType,
  parseDiscount,
} from './perk-form-modal.constants'
import { PERK_IMAGES_PATH } from './perks.constants'
import { getPerkCategoryOptions } from './perks.utils'

interface PerkFormModalProps {
  buildingId: number
  onClose: VoidFunction
  perk?: NeighbourhoodPerk
}

export const PerkFormModal = ({ buildingId, onClose, perk }: PerkFormModalProps) => {
  const { t } = useTranslation(['organisation', 'neighborhood-perks', 'common'])
  const { mutateAsync: createPerk } = useCreateNeighbourhoodPerk()
  const { mutateAsync: updatePerk } = useUpdateNeighbourhoodPerk()
  const isEdit = !!perk

  const schema = useMemo(
    () => makePerkFormSchema(t, { hasExistingImage: !!perk?.image, hasExistingLogo: !!perk?.logo }),
    [t, perk],
  )

  const categoryOptions = useMemo<SelectOption<string>[]>(
    () => getPerkCategoryOptions((key) => t(`neighborhood-perks:categories.${key}`)),
    [t],
  )

  const typeOptions = useMemo<SelectOption<PerkType>[]>(
    () => [
      { label: t('organisation:places.perks.types.promoCode'), value: PerkType.link },
      { label: t('organisation:places.perks.types.website'), value: PerkType.url },
    ],
    [t],
  )

  const discountTypeOptions = useMemo<SelectOption<DiscountType>[]>(
    () =>
      Object.values(DiscountType).map((value) => ({
        label: t(`organisation:places.perks.form.discount.types.${value}`),
        value,
      })),
    [t],
  )

  const form = useForm<PerkFormData>({
    defaultValues: getPerkFormDefaults(perk),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState
  const perkType = form.watch('perkType')
  const discountType = form.watch('discountType')

  // Note: each discount type keeps its own value while the modal is open; switching type empties the field
  const discountValues = useRef<Partial<Record<DiscountType, string>>>({})
  const previousDiscountType = useRef(discountType)

  useEffect(() => {
    if (previousDiscountType.current === discountType) return
    discountValues.current[previousDiscountType.current] = form.getValues('discountValue')
    previousDiscountType.current = discountType
    form.setValue('discountValue', discountValues.current[discountType] ?? '', {
      shouldDirty: true,
    })
    form.clearErrors('discountValue')
  }, [discountType, form])

  const changeFields = useMemo<FormChangeFields<PerkFormData>>(
    () => ({
      category: { label: t('neighborhood-perks:form.category.label') },
      description: { label: t('neighborhood-perks:form.description.label') },
      discountType: { label: t('neighborhood-perks:form.tag.label') },
      discountValue: { label: t('neighborhood-perks:form.tag.label') },
      image: { image: true, label: t('neighborhood-perks:form.image.label') },
      link: { label: t('organisation:places.perks.types.promoCode') },
      logo: { image: true, label: t('neighborhood-perks:form.logo.label') },
      name: { label: t('neighborhood-perks:form.name.label') },
      url: { label: t('organisation:places.perks.types.website') },
    }),
    [t],
  )

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<PerkFormData> = async (data) => {
    // Note: the stored tag is kept unless the discount was edited, so a legacy "10%" is not rewritten as "-10%"
    const stored = perk ? parseDiscount(perk.tag) : undefined
    const isDiscountChanged =
      !stored || stored.type !== data.discountType || stored.value !== data.discountValue.trim()

    const payload = {
      buildingId,
      category: data.category,
      description: data.description.trim(),
      image: data.image?.[0],
      logo: data.logo?.[0],
      name: data.name.trim(),
      tag:
        perk && !isDiscountChanged
          ? perk.tag
          : buildDiscount(data.discountType, data.discountValue),
      ...(data.perkType === PerkType.url
        ? { url: normalizeLink(data.url ?? '') }
        : { link: data.link?.trim() }),
    }

    try {
      if (perk) {
        await updatePerk({ ...payload, id: perk.id })
        toastFactory.success(t('organisation:places.perks.toasts.updated'))
      } else {
        await createPerk(payload)
        toastFactory.success(t('organisation:places.perks.toasts.created'))
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const submitDisabled = isEdit && !isDirty

  return (
    <Modal
      autoWidth
      title={
        isEdit
          ? t('organisation:places.perks.form.titleEdit')
          : t('organisation:places.perks.form.titleNew')
      }
      headerIcon={StorefrontIcon}
      footer={
        <>
          <Button use="secondary" onClick={handleClose}>
            {isEdit ? t('common:actions.cancel') : t('common:actions.back')}
          </Button>
          <Button disabled={submitDisabled} loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout submitDisabled={submitDisabled} form={form} onSubmit={submit}>
        <AvatarFormField
          existingImageUrl={
            perk?.logo ? getAssetLink({ file: perk.logo, path: PERK_IMAGES_PATH }) : undefined
          }
          label={t('neighborhood-perks:form.logo.label')}
          name="logo"
          uploadLabel={t('neighborhood-perks:form.logo.label')}
        />

        <AvatarFormField
          existingImageUrl={
            perk?.image ? getAssetLink({ file: perk.image, path: PERK_IMAGES_PATH }) : undefined
          }
          label={t('neighborhood-perks:form.image.label')}
          name="image"
          uploadLabel={t('neighborhood-perks:form.image.label')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('neighborhood-perks:form.name.label')}
            maxLength={PERK_TEXT_MAX_LENGTH}
            name="name"
            placeholder={t('neighborhood-perks:form.name.placeholder')}
          />
          <SelectFormField<PerkFormData, string>
            withReservedFooter
            required
            label={t('neighborhood-perks:form.category.label')}
            name="category"
            options={categoryOptions}
            placeholder={t('organisation:places.perks.form.category.placeholder')}
            size="medium"
            type="single"
          />
        </Grid>

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<PerkFormData, PerkType>
            withReservedFooter
            label={t('neighborhood-perks:form.perkType.label')}
            name="perkType"
            options={typeOptions}
            size="medium"
            type="single"
          />
          {perkType === PerkType.url ? (
            <TextInputFormField
              withReservedFooter
              required
              label={t('organisation:places.perks.types.website')}
              maxLength={PERK_TEXT_MAX_LENGTH}
              name="url"
              placeholder={t('neighborhood-perks:form.url.placeholder')}
            />
          ) : (
            <TextInputFormField
              withReservedFooter
              required
              label={t('organisation:places.perks.types.promoCode')}
              maxLength={PERK_TEXT_MAX_LENGTH}
              name="link"
              placeholder={t('neighborhood-perks:form.link.placeholder')}
            />
          )}
        </Grid>

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<PerkFormData, DiscountType>
            withReservedFooter
            label={t('neighborhood-perks:form.tag.label')}
            name="discountType"
            options={discountTypeOptions}
            size="medium"
            type="single"
          />
          {discountType === DiscountType.percent && (
            <NumericInputFormField
              withReservedFooter
              required
              leadingSlot={DISCOUNT_SYMBOLS.percent}
              label={t('organisation:places.perks.form.discount.value.percent')}
              max={PERCENT_DISCOUNT_MAX}
              min={1}
              mode="integer"
              name="discountValue"
            />
          )}
          {discountType === DiscountType.amount && (
            <NumericInputFormField
              withReservedFooter
              required
              leadingSlot={DISCOUNT_SYMBOLS.amount}
              label={t('organisation:places.perks.form.discount.value.amount')}
              max={AMOUNT_DISCOUNT_MAX}
              min={1}
              mode="integer"
              name="discountValue"
            />
          )}
          {discountType === DiscountType.custom && (
            <TextInputFormField
              withReservedFooter
              required
              label={t('organisation:places.perks.form.discount.value.custom')}
              maxLength={PERK_DISCOUNT_LENGTH}
              name="discountValue"
              placeholder={t('neighborhood-perks:form.tag.placeholder')}
            />
          )}
        </Grid>

        <TextAreaFormField
          withReservedFooter
          required
          label={t('neighborhood-perks:form.description.label')}
          maxLength={PERK_DESCRIPTION_MAX_LENGTH}
          name="description"
          placeholder={t('neighborhood-perks:form.description.placeholder')}
        />
      </PersonFormLayout>
    </Modal>
  )
}
